import { selectItemInCart } from '@/app/modules/cart/state/common/entities/cart-item/cart-item.selectors';
import { PPTCart } from '@/app/modules/ppt/services/ppt-cart.service';
import { PPTDelegated } from '@/app/modules/ppt/services/ppt-delegated.service';
import { selectPPTAccount, selectPPTAccountAddAllButtonEnabled, selectPPTAccountAddAllButtonTitle, selectPPTAccountAddAllShowWarningIcon, selectPPTAccountHistory, selectPPTAccountIsInCart } from '@/app/modules/ppt/state/entities/account/ppt-account.selectors';
import { selectPPTBill, selectPPTBillCartButtonTitle, selectPPTBillIsByItselfPayable, selectPPTBillLink } from '@/app/modules/ppt/state/entities/bill/ppt-bill.selectors';
import { selectPPTBlockMessages, selectPPTDisplayPaymentHistory, selectPPTDisplayPdfParcelHistory, selectPPTDisplayPrintBillButton, selectPPTDisplayPrintReceiptButton, selectPPTDisplayReceiptId } from '@/app/modules/ppt/state/ppt-state/ppt-state.selectors';
import { NullLists } from '@/app/modules/shared/services/null-lists.service';
import { State } from '@/app/state/model';
import { Component, Input } from '@angular/core';
import { sync } from '@ces/sync';
import { Platform } from '@egovsolutions/angular-platform';
import { QuickModal } from '@egovsolutions/angular-quick-modal';
import { select, Store } from '@ngrx/store';
import { Transitions } from '@shared/services/transitions.service';
import { Viewport } from '@shared/services/viewport.service';
import { getStatusForBill, PPTBill, PPTBillPayment } from 'egov-api';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'ppt-parcel-history',
    templateUrl: './parcel-history.component.html',
    styleUrls: ['./parcel-history.component.scss'],
    animations:
    [
        Transitions.RETAIN_LEAVING_ELEMENT,
    ]
})
export class ParcelHistoryComponent
{
    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
        private readonly pptCart: PPTCart,
        private readonly quickModal: QuickModal,
        private readonly delegated: PPTDelegated,


        // Template dependencies

        protected readonly nullLists: NullLists,
        protected readonly viewport: Viewport,
        protected readonly platform: Platform,
    )
    {
        this.historyBillsAndPaymentsTrackBy = this.historyBillsAndPaymentsTrackBy.bind(this);
    }


    // Input properties

    @Input() protected set accountNumber(value: string | undefined) { this.accountNumber$.next(value); }
    protected readonly accountNumber$ = new BehaviorSubject<string | undefined>(undefined);

    @Input() protected taxYear?: number;
    @Input() protected historyPrintUrl?: string;
    @Input() protected loaded?: boolean;


    // Service state / settings

    protected readonly displayReceiptId$ = this.store.select(selectPPTDisplayReceiptId);
    protected readonly displayPrintBillButton$ = this.store.select(selectPPTDisplayPrintBillButton);


    // PPT Entities

    protected routerLink$(id: string) { return this.store.select(selectPPTBillLink(id)); }

    protected readonly account$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.pipe(select(selectPPTAccount(accountNumber)))
            : of(undefined)
        )
    );


    // Bill status

    protected readonly getStatusForBill = getStatusForBill;


    // Block messages

    protected readonly blockMessages$ = this.store.select(selectPPTBlockMessages);


    // Cart

    protected readonly totalAccountDue$ = this.account$.pipe(map(account => account?.due?.total));
    protected readonly addAllEnabled$ = this.accountNumber$.pipe(
        switchMap(aN => aN ? this.store.select(selectPPTAccountAddAllButtonEnabled(aN)) : of(undefined)),
    );
    protected readonly showAddAllWarningIcon$ = this.accountNumber$.pipe(
        switchMap(aN => aN ? this.store.select(selectPPTAccountAddAllShowWarningIcon(aN)) : of(undefined)),
    );

    protected readonly addAccountToOrRemoveFromCartTitle$ = this.accountNumber$.pipe(
        switchMap(aN => aN ? this.store.select(selectPPTAccountAddAllButtonTitle(aN)) : of('')),
    );

    protected billIsInCart$(bill: PPTBill) { return this.store.select(selectItemInCart(PPTCart.BILL_CART_TYPE, bill.id)); }

    protected isAccountInCart$ = this.accountNumber$.pipe(
        switchMap(aN => aN ? this.store.select(selectPPTAccountIsInCart(aN)) : of(undefined)),
    );

    private readonly billsBeingToggled$ = new BehaviorSubject(new Set<string>());

    private addBillBeingToggled(accountNumber: string)
    {
        this.billsBeingToggled$.next(
            new Set(this.billsBeingToggled$.value.add(accountNumber))
        );
    }

    private removeBillBeingToggled(accountNumber: string)
    {
        const set = new Set(this.billsBeingToggled$.value);
        set.delete(accountNumber);
        this.billsBeingToggled$.next(set);
    }

    protected toggleBillInCart(bill: PPTBill, e?: MouseEvent, raiseAttention: boolean = true)
    {
        if (e) e.stopPropagation();

        if (!bill?.paymentBlocked && !this.billsBeingToggled$.value.has(bill.id))
        {
            if (sync(this.delegated.billOrDepsDelegated$(bill.id)))
            {
                const message = sync(this.delegated.delegatedMessage$(bill.id));
                this.quickModal.show(message || 'This bill cannot be added to the cart.', true);
            }
            else
            {
                this.addBillBeingToggled(bill.id);

                this.pptCart.toggleBillInCart(
                    bill.id,
                    true,
                    raiseAttention,
                    () => this.toggleBillInCart(bill, e, raiseAttention),
                ).subscribe({
                    complete: () => this.removeBillBeingToggled(bill.id),
                    error: () => this.removeBillBeingToggled(bill.id),
                });
            }
        }
    }

    protected readonly updatingAccountCartStatus$ = new BehaviorSubject(false);

    protected toggleAccountInCart()
    {
        if (this.accountNumber$.value && this.loaded && !this.updatingAccountCartStatus$.value)
        {
            this.updatingAccountCartStatus$.next(true);
            this.pptCart.toggleAccountInCart(this.accountNumber$.value, true).subscribe({
                complete: () => this.updatingAccountCartStatus$.next(false),
                error: () => this.updatingAccountCartStatus$.next(false),
            });
        }
    }


    // Other

    protected readonly displayPaymentHistory$ = this.store.select(selectPPTDisplayPaymentHistory);
    protected readonly displayPrintReceiptButton$ = this.store.select(selectPPTDisplayPrintReceiptButton);
    protected readonly printParcelHistoryAllowed$ = this.store.select(selectPPTDisplayPdfParcelHistory);

    protected getPrintReceiptTitle(bill: PPTBill): string | undefined
    {
        if (bill)
        {
            if (bill.payments?.length)
            {
                if (bill.payments.length === 1)
                    return this.getPaymentPrintReceiptTitle(bill.payments[0]);
                else
                    return 'This bill has multiple payments. Click to expand them.';
            }
            else
            {
                return 'This bill doesn\'t have any receipts yet.';
            }
        }
    }

    protected getPaymentPrintReceiptTitle(payment: PPTBillPayment): string
    {
        if (payment.printUrl)
            return 'Print Receipt';
        else
            return 'A receipt cannot be printed for this payment.';
    }

    protected readonly historyUnpaidOnly$ = new BehaviorSubject(true);

    protected onHistoryTabChange(index: number)
    {
        this.historyUnpaidOnly$.next(!index);
    }

    private readonly billsShowingDetailsFor: Set<string> = new Set<string>();

    protected readonly billsWithPaymentsExpanded$ = new BehaviorSubject(new Set<string>());

    private readonly history$: Observable<PPTBill[] | undefined> = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.select(selectPPTAccountHistory(accountNumber)).pipe(
                switchMap(billIds => billIds
                    ? combineLatest(billIds.map(id =>
                            combineLatest([
                                this.store.select(selectPPTBill(id)),
                                this.store.select(selectPPTBillCartButtonTitle(id)),
                                this.store.select(selectPPTBillIsByItselfPayable(id, true)),
                                this.billsBeingToggled$,
                            ]).pipe(
                                filter(([bill]) => !!bill),
                                map(([bill, title, isItselfPayable, billsBeingToggled]) => ({
                                    ...bill!,
                                    title,
                                    isItselfPayable,
                                    toggling: billsBeingToggled.has(id),
                                }))
                            )
                        )).pipe(
                            map(bills => bills.filter(b => b)),
                            map(bills => bills.sort((a, b) => b.taxYear - a.taxYear)),
                        )
                    : of(undefined)
                )
            )
            : of(undefined)
        ),
    );

    protected readonly filteredHistory$ = combineLatest([this.history$, this.historyUnpaidOnly$]).pipe(
        map(([bills, unpaidOnly]) => unpaidOnly ? bills?.filter(b => !b.paid) : bills)
    );

    protected filteredHistoryTrackBy(index: number, item: null | PPTBill): number | string
    {
        if (item === null) return `${index}`;
        return `bill-${item.id}`;
    }

    protected readonly historyBillsAndPayments$ =
        combineLatest([this.filteredHistory$, this.billsWithPaymentsExpanded$]).pipe(
            map(([bills, billsWithPaymentsExpanded]) => bills?.reduce(
                (out, bill) => billsWithPaymentsExpanded.has(bill.id) && bill.payments
                    ? [...out, bill, ...bill.payments]
                    : [...out, bill],
                [] as (PPTBill | PPTBillPayment)[]
            )),
        );

    protected historyBillsAndPaymentsTrackBy(index: number, item: null | PPTBill | PPTBillPayment): number | string
    {
        if (item === null) return `${index}`;
        return this.isBill(item) ? `bill-${item.id}` : `payment-${item.receipt}`;
    }

    private typeOfItem(item: PPTBill | PPTBillPayment): string
    {
        return !item || 'parcelId' in item ? 'bill' : 'payment';
    }

    protected isBill(item: PPTBill | PPTBillPayment): item is PPTBill
    {
        return this.typeOfItem(item) === 'bill';
    }

    protected isPayment(item: PPTBill | PPTBillPayment): item is PPTBillPayment
    {
        return this.typeOfItem(item) === 'payment';
    }

    protected shouldBillHaveItsReceiptButtonDisabled(bill: PPTBill): boolean
    {
        return (
        !(
            bill?.payments
            &&
            (
                bill.payments.length === 1
                &&
                !!bill.payments[0].printUrl

                ||

                bill.payments.length > 1
            )
        ));
    }

    protected showingDetails(bill: PPTBill): boolean { return bill && this.billsShowingDetailsFor.has(bill.id); }


    protected receiptsToggleHandler(e: Event, bill: PPTBill)
    {
        e.stopPropagation();

        const clonedSet = new Set(this.billsWithPaymentsExpanded$.value);

        if (!clonedSet.has(bill.id))
        {
            clonedSet.add(bill.id);
            this.billsWithPaymentsExpanded$.next(clonedSet);
        }
        else
        {
            clonedSet.delete(bill.id);
            this.billsWithPaymentsExpanded$.next(clonedSet);
        }
    }


    protected billReceiptButtonClickHandler(e: Event, bill: PPTBill)
    {
        e.stopPropagation();

        if (bill)
        {
            if (bill.payments?.length)
            {
                if (bill.payments.length === 1)
                    this.paymentReceiptButtonClickHandler(bill.payments[0]);
                else
                {
                    const clonedSet = new Set(this.billsWithPaymentsExpanded$.value);

                    if (!clonedSet.has(bill.id))
                    {
                        clonedSet.add(bill.id);
                        this.billsWithPaymentsExpanded$.next(clonedSet);
                    }
                    else
                    {
                        clonedSet.delete(bill.id);
                        this.billsWithPaymentsExpanded$.next(clonedSet);
                    }
                }
            }
        }
    }

    protected paymentReceiptButtonClickHandler(payment: PPTBillPayment, e?: Event)
    {
        if (e) e.stopPropagation();

        if (payment.printUrl)
            window.open(payment.printUrl);
    }

    protected printBill(e: Event, bill: PPTBill)
    {
        e.stopPropagation();
        if (bill?.printUrl) window.open(bill.printUrl);
    }

    protected toggleDetailsForBill(bill: PPTBill)
    {
        if (bill)
            if (!this.billsShowingDetailsFor.has(bill.id))
                this.billsShowingDetailsFor.add(bill.id);
            else
                this.billsShowingDetailsFor.delete(bill.id);
    }
}
